'use client';

import React from 'react';
import Link from 'next/link';
import { CalenddyLogo } from '@/components/brand/CalenddyLogo';
import {
  Calendar,
  Clock,
  Smartphone,
  CreditCard,
  Users,
  BarChart3,
  CheckCircle2,
  ArrowRight,
  ShieldCheck,
  Sparkles,
  Zap,
  MessageCircle,
  Scissors,
  Building2,
  ChevronRight,
  HelpCircle
} from 'lucide-react';

export default function CalenddyLandingPage() {
  return (
    <div className="min-h-screen bg-[#F7F7FA] text-[#191923] flex flex-col font-sans selection:bg-[#6255F5] selection:text-white">
      {/* 1. NAVBAR COMERCIAL */}
      <nav className="w-full bg-white/90 backdrop-blur-md border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-20 flex items-center justify-between">
          <CalenddyLogo href="/" size="md" />

          {/* Links de Navegación de Escritorio */}
          <div className="hidden md:flex items-center gap-8 text-sm font-semibold text-slate-600">
            <Link href="/funciones" className="hover:text-[#6255F5] transition-colors">
              Funciones
            </Link>
            <Link href="/precios" className="hover:text-[#6255F5] transition-colors">
              Precios
            </Link>
            <a href="#integraciones" className="hover:text-[#6255F5] transition-colors">
              Integraciones
            </a>
            <a href="#como-funciona" className="hover:text-[#6255F5] transition-colors">
              Cómo funciona
            </a>
            <a href="#faq" className="hover:text-[#6255F5] transition-colors">
              Preguntas
            </a>
          </div>

          {/* CTAs de Acceso y Registro */}
          <div className="flex items-center gap-3">
            <Link
              href="/ingresar"
              className="text-xs sm:text-sm font-bold text-slate-700 hover:text-[#6255F5] px-3 py-2 rounded-xl transition-colors"
            >
              Ingresar
            </Link>
            <Link
              href="/registro"
              className="px-4 sm:px-5 py-2.5 bg-[#6255F5] hover:bg-[#5144E5] text-white text-xs sm:text-sm font-bold rounded-xl transition-all shadow-md shadow-[#6255F5]/25 hover:shadow-lg hover:shadow-[#6255F5]/30 active:scale-98 flex items-center gap-1.5"
            >
              <span>Empezar con Calenddy</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </nav>

      {/* 2. HERO PRINCIPAL */}
      <section className="relative pt-12 pb-20 sm:pt-20 sm:pb-28 overflow-hidden">
        {/* Soft Violet Radial Background Glow */}
        <div className="absolute top-10 left-1/2 -translate-x-1/2 w-[700px] h-[350px] bg-[#6255F5]/8 blur-[100px] pointer-events-none rounded-full" />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center space-y-6 relative z-10">
          {/* Badge de Plataforma SaaS */}
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#ECEAFF] border border-[#d6d0ff] text-[#6255F5] text-xs font-bold shadow-xs">
            <Sparkles className="w-4 h-4 text-[#6255F5]" />
            <span>Plataforma SaaS de Reservas y Gestión para Negocios</span>
          </div>

          {/* Título Principal Requerido */}
          <h1 className="text-4xl sm:text-6xl font-black text-[#191923] tracking-tight leading-[1.1] max-w-4xl mx-auto">
            Tu agenda. Tu negocio. <br className="hidden sm:inline" />
            <span className="text-[#6255F5]">Todo conectado.</span>
          </h1>

          {/* Descripción Requerida */}
          <p className="text-base sm:text-xl text-slate-600 max-w-2xl mx-auto font-normal leading-relaxed">
            Gestioná turnos, profesionales, clientes, cobros y WhatsApp en una sola plataforma.
          </p>

          {/* Botones de Conversión */}
          <div className="pt-4 flex flex-col sm:flex-row items-center justify-center gap-3 max-w-md mx-auto">
            <Link
              href="/registro"
              className="w-full sm:w-auto px-7 py-3.5 bg-[#6255F5] hover:bg-[#5144E5] text-white font-bold text-sm rounded-2xl transition-all shadow-lg shadow-[#6255F5]/25 hover:shadow-xl hover:shadow-[#6255F5]/35 flex items-center justify-center gap-2"
            >
              <span>Empezar con Calenddy</span>
              <ArrowRight className="w-4 h-4" />
            </Link>

            <Link
              href="/funciones"
              className="w-full sm:w-auto px-7 py-3.5 bg-white hover:bg-slate-50 text-slate-700 font-bold text-sm rounded-2xl border border-slate-200 transition-all shadow-xs flex items-center justify-center gap-2"
            >
              <span>Explorar funciones</span>
            </Link>
          </div>

          {/* Trust Highlights */}
          <div className="pt-6 flex flex-wrap items-center justify-center gap-6 text-xs font-medium text-slate-500">
            <div className="flex items-center gap-1.5">
              <CheckCircle2 className="w-4 h-4 text-[#10B981]" />
              <span>Sin comisiones abusivas</span>
            </div>
            <div className="flex items-center gap-1.5">
              <CheckCircle2 className="w-4 h-4 text-[#10B981]" />
              <span>WhatsApp Web propio con QR</span>
            </div>
            <div className="flex items-center gap-1.5">
              <CheckCircle2 className="w-4 h-4 text-[#10B981]" />
              <span>Señas automáticas con Mercado Pago</span>
            </div>
          </div>
        </div>

        {/* 3. DEMOSTRACIÓN VISUAL (Agenda Escritorio + Turnos Móvil) */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-14">
          <div className="relative bg-white rounded-3xl p-4 sm:p-6 shadow-2xl border border-slate-200/90 overflow-hidden">
            {/* Browser Header Bar */}
            <div className="flex items-center justify-between pb-4 border-b border-slate-100 text-xs text-slate-400">
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-rose-400" />
                <span className="w-3 h-3 rounded-full bg-amber-400" />
                <span className="w-3 h-3 rounded-full bg-emerald-400" />
                <span className="ml-2 font-mono text-[11px] text-slate-500 bg-slate-100 px-3 py-1 rounded-md">
                  https://calenddy.com/dashboard/bookings
                </span>
              </div>
              <div className="hidden sm:flex items-center gap-2 font-semibold text-[#6255F5]">
                <Sparkles className="w-3.5 h-3.5" />
                <span>Vista en tiempo real</span>
              </div>
            </div>

            {/* Mock Composición: Agenda + Widget Móvil */}
            <div className="pt-6 grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
              {/* Desktop Agenda Preview */}
              <div className="lg:col-span-8 bg-[#F7F7FA] rounded-2xl p-5 border border-slate-200/70 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <div className="w-9 h-9 rounded-xl bg-[#6255F5] text-white flex items-center justify-center font-bold">
                      <Calendar className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-slate-900">Agenda de Profesionales</h4>
                      <p className="text-xs text-slate-500">Miércoles, 14 de Octubre de 2026</p>
                    </div>
                  </div>
                  <span className="px-3 py-1 bg-emerald-50 text-emerald-700 text-xs font-bold rounded-lg border border-emerald-200">
                    4 Turnos Hoy
                  </span>
                </div>

                {/* Turnos en columna */}
                <div className="space-y-2.5">
                  <div className="p-3 bg-white rounded-xl border border-slate-200 flex items-center justify-between shadow-xs">
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-mono font-bold text-[#6255F5] bg-[#ECEAFF] px-2 py-1 rounded-md">
                        14:00
                      </span>
                      <div>
                        <p className="text-xs font-bold text-slate-900">Lucas Méndez — Corte Clásico & Barba</p>
                        <p className="text-[11px] text-slate-400">Atiende: Juan Rojas · Seña $8.000 (Acreditada MP)</p>
                      </div>
                    </div>
                    <span className="text-[11px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md">
                      Confirmado
                    </span>
                  </div>

                  <div className="p-3 bg-white rounded-xl border border-slate-200 flex items-center justify-between shadow-xs">
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-mono font-bold text-[#6255F5] bg-[#ECEAFF] px-2 py-1 rounded-md">
                        15:00
                      </span>
                      <div>
                        <p className="text-xs font-bold text-slate-900">Facundo Gómez — Degradé con Lavado</p>
                        <p className="text-[11px] text-slate-400">Atiende: Liliana · Seña $10.000 (Efectivo)</p>
                      </div>
                    </div>
                    <span className="text-[11px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md">
                      Confirmado
                    </span>
                  </div>

                  <div className="p-3 bg-white rounded-xl border border-slate-200 flex items-center justify-between shadow-xs">
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-mono font-bold text-[#6255F5] bg-[#ECEAFF] px-2 py-1 rounded-md">
                        16:15
                      </span>
                      <div>
                        <p className="text-xs font-bold text-slate-900">Martín Rossi — Ritual de Barba</p>
                        <p className="text-[11px] text-slate-400">Atiende: Juan Rojas · Recordatorio WhatsApp enviado</p>
                      </div>
                    </div>
                    <span className="text-[11px] font-bold text-blue-700 bg-blue-50 px-2 py-0.5 rounded-md">
                      En Curso
                    </span>
                  </div>
                </div>
              </div>

              {/* Mobile Phone Mockup: Portal de Reservas Público */}
              <div className="lg:col-span-4 bg-[#191923] text-white rounded-2xl p-5 border border-slate-800 shadow-xl space-y-4">
                <div className="flex items-center justify-between text-xs text-slate-400">
                  <span className="flex items-center gap-1 font-mono text-[10px]">
                    <Smartphone className="w-3.5 h-3.5 text-[#6255F5]" />
                    calenddy.com/juanrojas
                  </span>
                  <span className="text-[10px] bg-[#6255F5] text-white px-2 py-0.5 rounded-full font-bold">
                    Público
                  </span>
                </div>

                <div className="bg-white text-[#191923] rounded-xl p-4 space-y-3 shadow-inner">
                  <div className="text-center">
                    <div className="w-12 h-12 mx-auto rounded-full bg-[#6255F5] text-white flex items-center justify-center font-black text-lg">
                      JR
                    </div>
                    <h5 className="font-bold text-xs mt-1">Juan Rojas Barber Studio</h5>
                    <p className="text-[10px] text-slate-500">Elegí tu servicio y horario en 3 clics</p>
                  </div>

                  <div className="border border-slate-200 rounded-lg p-2.5 text-xs flex items-center justify-between">
                    <div>
                      <p className="font-bold">Corte Clásico</p>
                      <p className="text-[10px] text-slate-500">45 min · $25.000</p>
                    </div>
                    <span className="text-[10px] font-bold text-[#6255F5] bg-[#ECEAFF] px-2 py-1 rounded-md">
                      Elegir
                    </span>
                  </div>

                  <Link
                    href="/juanrojas"
                    target="_blank"
                    className="w-full py-2 bg-[#6255F5] text-white font-bold text-xs rounded-lg flex items-center justify-center gap-1.5 hover:bg-[#5144E5] transition"
                  >
                    <span>Ver demo real en vivo</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </div>

                <p className="text-[11px] text-slate-400 text-center">
                  Tus clientes reservan sin apps, sin cuentas y directo desde WhatsApp o Instagram.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4. BENEFICIOS Y MÓDULOS DE GESTIÓN */}
      <section id="funciones" className="py-20 bg-white border-y border-slate-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 space-y-12">
          <div className="text-center max-w-2xl mx-auto space-y-3">
            <span className="text-xs font-bold text-[#6255F5] tracking-wide uppercase">
              Todo en un solo lugar
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-[#191923] tracking-tight">
              Diseñado para negocios que valoran su tiempo
            </h2>
            <p className="text-sm sm:text-base text-slate-600">
              Cada herramienta de Calenddy está construida para resolver un problema operativo real, sin complejidad innecesaria.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Beneficio 1 */}
            <div className="bg-[#F7F7FA] rounded-2xl p-6 border border-slate-200/80 space-y-3 hover:shadow-md transition">
              <div className="w-10 h-10 rounded-xl bg-[#ECEAFF] text-[#6255F5] flex items-center justify-center">
                <Clock className="w-5 h-5" />
              </div>
              <h3 className="text-base font-bold text-slate-900">Reservas online 24 horas</h3>
              <p className="text-xs text-slate-600 leading-relaxed">
                Tus clientes reservan en cualquier momento desde su celular con tu enlace propio <code className="text-[#6255F5] font-semibold">calenddy.com/tu-negocio</code>.
              </p>
            </div>

            {/* Beneficio 2 */}
            <div className="bg-[#F7F7FA] rounded-2xl p-6 border border-slate-200/80 space-y-3 hover:shadow-md transition">
              <div className="w-10 h-10 rounded-xl bg-[#ECEAFF] text-[#6255F5] flex items-center justify-center">
                <Calendar className="w-5 h-5" />
              </div>
              <h3 className="text-base font-bold text-slate-900">Agenda para múltiples profesionales</h3>
              <p className="text-xs text-slate-600 leading-relaxed">
                Gestioná horarios individuales, descansos, pausas entre servicios y bloqueos de vacaciones con sincronización en tiempo real.
              </p>
            </div>

            {/* Beneficio 3 */}
            <div className="bg-[#F7F7FA] rounded-2xl p-6 border border-slate-200/80 space-y-3 hover:shadow-md transition">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center">
                <MessageCircle className="w-5 h-5" />
              </div>
              <h3 className="text-base font-bold text-slate-900">WhatsApp y recordatorios automáticos</h3>
              <p className="text-xs text-slate-600 leading-relaxed">
                Vinculá tu propio número con código QR. Enviá confirmaciones, recordatorios 24h antes y avisos de reprogramación sin tocar el teléfono.
              </p>
            </div>

            {/* Beneficio 4 */}
            <div className="bg-[#F7F7FA] rounded-2xl p-6 border border-slate-200/80 space-y-3 hover:shadow-md transition">
              <div className="w-10 h-10 rounded-xl bg-blue-100 text-blue-700 flex items-center justify-center">
                <CreditCard className="w-5 h-5" />
              </div>
              <h3 className="text-base font-bold text-slate-900">Caja, señas y Mercado Pago</h3>
              <p className="text-xs text-slate-600 leading-relaxed">
                Cobrá señas automáticas para evitar cancelaciones de último momento o registrá cobros en efectivo con arqueo de caja diario.
              </p>
            </div>

            {/* Beneficio 5 */}
            <div className="bg-[#F7F7FA] rounded-2xl p-6 border border-slate-200/80 space-y-3 hover:shadow-md transition">
              <div className="w-10 h-10 rounded-xl bg-purple-100 text-purple-700 flex items-center justify-center">
                <Users className="w-5 h-5" />
              </div>
              <h3 className="text-base font-bold text-slate-900">Fichas de clientes e historial</h3>
              <p className="text-xs text-slate-600 leading-relaxed">
                Guardá el historial completo de visitas, notas de coloración o preferencias, asistencias y saldo de puntos de cada cliente.
              </p>
            </div>

            {/* Beneficio 6 */}
            <div className="bg-[#F7F7FA] rounded-2xl p-6 border border-slate-200/80 space-y-3 hover:shadow-md transition">
              <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center">
                <BarChart3 className="w-5 h-5" />
              </div>
              <h3 className="text-base font-bold text-slate-900">Estadísticas y gestión comercial</h3>
              <p className="text-xs text-slate-600 leading-relaxed">
                Conocé la tasa de ocupación de tu equipo, los servicios más pedidos, la facturación mensual y el rendimiento de tu negocio.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 5. CÓMO FUNCIONA (4 PASOS) */}
      <section id="como-funciona" className="py-20 bg-[#F7F7FA]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 space-y-12">
          <div className="text-center max-w-xl mx-auto space-y-2">
            <span className="text-xs font-bold text-[#6255F5] uppercase tracking-wide">Paso a paso</span>
            <h2 className="text-3xl font-extrabold text-[#191923] tracking-tight">
              Empezá en menos de 5 minutos
            </h2>
            <p className="text-xs sm:text-sm text-slate-500">
              Sin configuraciones complejas ni necesidad de conocimientos técnicos.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-2xl p-5 border border-slate-200/80 space-y-3 shadow-xs">
              <div className="w-8 h-8 rounded-full bg-[#6255F5] text-white flex items-center justify-center text-xs font-black">
                1
              </div>
              <h4 className="text-sm font-bold text-slate-900">Creá tu cuenta</h4>
              <p className="text-xs text-slate-500 leading-relaxed">
                Ingresá los datos básicos de tu negocio y elegí tu dirección <code className="text-[#6255F5]">calenddy.com/tu-slug</code>.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-5 border border-slate-200/80 space-y-3 shadow-xs">
              <div className="w-8 h-8 rounded-full bg-[#6255F5] text-white flex items-center justify-center text-xs font-black">
                2
              </div>
              <h4 className="text-sm font-bold text-slate-900">Cargá tus servicios</h4>
              <p className="text-xs text-slate-500 leading-relaxed">
                Definí precios, duraciones, profesionales disponibles y si querés cobrar seña previa.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-5 border border-slate-200/80 space-y-3 shadow-xs">
              <div className="w-8 h-8 rounded-full bg-[#6255F5] text-white flex items-center justify-center text-xs font-black">
                3
              </div>
              <h4 className="text-sm font-bold text-slate-900">Compartí tu enlace</h4>
              <p className="text-xs text-slate-500 leading-relaxed">
                Pegalo en tu biografía de Instagram, en tu Google Maps o configuralo en respuestas de WhatsApp.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-5 border border-slate-200/80 space-y-3 shadow-xs">
              <div className="w-8 h-8 rounded-full bg-[#6255F5] text-white flex items-center justify-center text-xs font-black">
                4
              </div>
              <h4 className="text-sm font-bold text-slate-900">Recibí turnos y cobrá</h4>
              <p className="text-xs text-slate-500 leading-relaxed">
                Tu agenda se completa automáticamente y el cliente recibe confirmación inmediata por WhatsApp.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 6. INTEGRACIONES REALES */}
      <section id="integraciones" className="py-16 bg-white border-t border-slate-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="bg-[#191923] text-white rounded-3xl p-8 sm:p-12 shadow-xl grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7 space-y-4">
              <span className="text-xs font-bold text-[#6255F5] uppercase tracking-wide">
                Integraciones Nativas
              </span>
              <h3 className="text-2xl sm:text-3xl font-black tracking-tight text-white">
                Conectá WhatsApp y Mercado Pago sin intermediarios
              </h3>
              <p className="text-xs sm:text-sm text-slate-300 leading-relaxed">
                En Calenddy cada negocio administra su propia instancia de WhatsApp mediante escaneo QR y recibe el dinero de las señas directamente en su cuenta de Mercado Pago.
              </p>
              <div className="pt-2 flex flex-wrap gap-4 text-xs font-semibold text-slate-200">
                <span className="inline-flex items-center gap-1.5 bg-white/10 px-3 py-1.5 rounded-xl">
                  <ShieldCheck className="w-4 h-4 text-emerald-400" />
                  Evolution API Baileys v2.3.7
                </span>
                <span className="inline-flex items-center gap-1.5 bg-white/10 px-3 py-1.5 rounded-xl">
                  <ShieldCheck className="w-4 h-4 text-blue-400" />
                  Webhooks Oficiales de Mercado Pago
                </span>
              </div>
            </div>

            <div className="lg:col-span-5 bg-white/5 border border-white/10 rounded-2xl p-6 text-center space-y-3">
              <div className="w-12 h-12 mx-auto rounded-2xl bg-[#6255F5] text-white flex items-center justify-center font-bold">
                <Zap className="w-6 h-6" />
              </div>
              <h4 className="text-sm font-bold text-white">Pruebas en tiempo real</h4>
              <p className="text-xs text-slate-400">
                Podés auditar el estado de los webhooks y la conexión QR desde el panel administrativo.
              </p>
              <Link
                href="/dashboard/whatsapp"
                className="inline-block text-xs font-bold text-[#6255F5] hover:text-white transition"
              >
                Abrir Automatización WhatsApp →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* 7. PLANES Y PRECIOS SAAS PREVIEW (Con distinción clara de precios de servicios) */}
      <section className="py-20 bg-[#F7F7FA] border-t border-slate-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 space-y-10">
          <div className="text-center max-w-xl mx-auto space-y-2">
            <span className="text-xs font-bold text-[#6255F5] uppercase tracking-wide">Planes SaaS</span>
            <h2 className="text-3xl font-extrabold text-[#191923] tracking-tight">
              Precios claros para tu software de gestión
            </h2>
            <p className="text-xs sm:text-sm text-slate-500">
              Estos son los costos de suscripción a Calenddy como plataforma. Los precios de los servicios de cada negocio se configuran libremente en su propia agenda.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Plan Inicial */}
            <div className="bg-white rounded-3xl p-6 border border-slate-200/90 shadow-sm flex flex-col justify-between space-y-6">
              <div className="space-y-4">
                <span className="text-xs font-bold text-slate-500 uppercase tracking-wide">Para Profesionales</span>
                <h3 className="text-xl font-bold text-slate-900">Plan Inicial</h3>
                <div className="flex items-baseline gap-1">
                  <span className="text-3xl font-black text-slate-900">$18.900</span>
                  <span className="text-xs text-slate-500">/ mes (ARS)</span>
                </div>
                <p className="text-xs text-slate-500">
                  Ideal para barberos, estilistas, terapeutas o profesionales independientes.
                </p>
                <ul className="space-y-2 pt-2 text-xs text-slate-600">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                    <span>1 Profesional incluido</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                    <span>Reservas online ilimitadas</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                    <span>Enlace personalizado <code className="text-[#6255F5]">/tu-slug</code></span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                    <span>WhatsApp Recordatorios automáticos</span>
                  </li>
                </ul>
              </div>

              <Link
                href="/precios"
                className="w-full py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs rounded-xl transition text-center"
              >
                Ver detalles del plan
              </Link>
            </div>

            {/* Plan Pro (Destacado) */}
            <div className="bg-white rounded-3xl p-6 border-2 border-[#6255F5] shadow-xl relative flex flex-col justify-between space-y-6">
              <div className="absolute -top-3 right-6 bg-[#6255F5] text-white text-[10px] font-black uppercase tracking-wider px-3 py-1 rounded-full shadow-sm">
                Más Elegido
              </div>

              <div className="space-y-4">
                <span className="text-xs font-bold text-[#6255F5] uppercase tracking-wide">Para Salones y Estudios</span>
                <h3 className="text-xl font-bold text-slate-900">Plan Profesional</h3>
                <div className="flex items-baseline gap-1">
                  <span className="text-3xl font-black text-[#6255F5]">$34.900</span>
                  <span className="text-xs text-slate-500">/ mes (ARS)</span>
                </div>
                <p className="text-xs text-slate-500">
                  Para salones, estudios de estética, peluquerías y consultorios con equipo.
                </p>
                <ul className="space-y-2 pt-2 text-xs text-slate-600">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-[#6255F5] shrink-0" />
                    <span className="font-semibold text-slate-900">Hasta 5 Profesionales</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-[#6255F5] shrink-0" />
                    <span>Cobro de señas con Mercado Pago</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-[#6255F5] shrink-0" />
                    <span>Caja diaria, ventas y control de stock</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-[#6255F5] shrink-0" />
                    <span>WhatsApp QR multi-dispositivo dedicado</span>
                  </li>
                </ul>
              </div>

              <Link
                href="/precios"
                className="w-full py-2.5 bg-[#6255F5] hover:bg-[#5144E5] text-white font-bold text-xs rounded-xl transition text-center shadow-md shadow-[#6255F5]/25"
              >
                Explorar todos los planes →
              </Link>
            </div>

            {/* Plan Multi-Equipo */}
            <div className="bg-white rounded-3xl p-6 border border-slate-200/90 shadow-sm flex flex-col justify-between space-y-6">
              <div className="space-y-4">
                <span className="text-xs font-bold text-slate-500 uppercase tracking-wide">Multi-Sucursal</span>
                <h3 className="text-xl font-bold text-slate-900">Plan Empresa</h3>
                <div className="flex items-baseline gap-1">
                  <span className="text-3xl font-black text-slate-900">$59.900</span>
                  <span className="text-xs text-slate-500">/ mes (ARS)</span>
                </div>
                <p className="text-xs text-slate-500">
                  Para franquicias, clínicas o marcas con múltiples sucursales y más de 10 profesionales.
                </p>
                <ul className="space-y-2 pt-2 text-xs text-slate-600">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                    <span>Profesionales ilimitados</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                    <span>Multi-sucursal y multi-caja</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                    <span>Métricas avanzadas y exportación CSV</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                    <span>Soporte prioritario por WhatsApp</span>
                  </li>
                </ul>
              </div>

              <Link
                href="/precios"
                className="w-full py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs rounded-xl transition text-center"
              >
                Ver detalles del plan
              </Link>
            </div>
          </div>

          <p className="text-center text-xs text-slate-400">
            * Planes y valores provisionales sujetos a confirmación de lanzamiento oficial. Sin permanencia ni contratos forzados.
          </p>
        </div>
      </section>

      {/* 8. PREGUNTAS FRECUENTES (FAQ) */}
      <section id="faq" className="py-20 bg-white border-t border-slate-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 space-y-10">
          <div className="text-center space-y-2">
            <span className="text-xs font-bold text-[#6255F5] uppercase tracking-wide">Dudas comunes</span>
            <h2 className="text-3xl font-extrabold text-[#191923] tracking-tight">
              Preguntas frecuentes
            </h2>
          </div>

          <div className="space-y-4">
            <div className="bg-[#F7F7FA] rounded-2xl p-5 border border-slate-200/80 space-y-2">
              <h4 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                <HelpCircle className="w-4 h-4 text-[#6255F5]" />
                ¿Mis clientes necesitan descargar una aplicación para reservar?
              </h4>
              <p className="text-xs text-slate-600 leading-relaxed pl-6">
                No. Tus clientes acceden directamente desde cualquier navegador web a través de tu enlace <code className="text-[#6255F5]">calenddy.com/tu-negocio</code>. No necesitan instalar nada ni crear contraseñas.
              </p>
            </div>

            <div className="bg-[#F7F7FA] rounded-2xl p-5 border border-slate-200/80 space-y-2">
              <h4 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                <HelpCircle className="w-4 h-4 text-[#6255F5]" />
                ¿Cómo funciona la conexión de WhatsApp?
              </h4>
              <p className="text-xs text-slate-600 leading-relaxed pl-6">
                Vinculás tu WhatsApp de forma directa escaneando un código QR desde la sección <strong>WhatsApp → Automatización</strong> en tu panel de control. Los mensajes salen desde tu propio número y tus clientes pueden responderte normalmente.
              </p>
            </div>

            <div className="bg-[#F7F7FA] rounded-2xl p-5 border border-slate-200/80 space-y-2">
              <h4 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                <HelpCircle className="w-4 h-4 text-[#6255F5]" />
                ¿El dinero de las señas entra a mi cuenta de Mercado Pago?
              </h4>
              <p className="text-xs text-slate-600 leading-relaxed pl-6">
                Sí. Conectás tu cuenta de Mercado Pago y el dinero de los anticipos se acredita inmediatamente en tu cuenta personal o de empresa, sin retenciones de Calenddy.
              </p>
            </div>

            <div className="bg-[#F7F7FA] rounded-2xl p-5 border border-slate-200/80 space-y-2">
              <h4 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                <HelpCircle className="w-4 h-4 text-[#6255F5]" />
                ¿Puedo tener horarios diferentes para cada profesional?
              </h4>
              <p className="text-xs text-slate-600 leading-relaxed pl-6">
                Totalmente. Cada barbero, estilista o profesional tiene su propia grilla de disponibilidad, días de trabajo, descansos y bloqueos de agenda independientes.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 9. BANNER CTA FINAL */}
      <section className="py-16 bg-[#6255F5] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center space-y-6">
          <h2 className="text-3xl sm:text-4xl font-black tracking-tight">
            Empezá a ordenar tu agenda hoy mismo con Calenddy
          </h2>
          <p className="text-sm sm:text-base text-white/80 max-w-xl mx-auto">
            Configurá tu negocio en 5 minutos y recibí reservas las 24 horas sin perder tiempo respondiendo manualmente.
          </p>
          <div className="pt-2 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              href="/registro"
              className="px-8 py-3.5 bg-white text-[#6255F5] hover:bg-slate-50 font-black text-sm rounded-2xl transition shadow-xl"
            >
              Crear mi cuenta gratis
            </Link>
            <Link
              href="/ingresar"
              className="px-8 py-3.5 bg-white/10 hover:bg-white/20 text-white font-bold text-sm rounded-2xl transition border border-white/20"
            >
              Acceso a mi panel
            </Link>
          </div>
        </div>
      </section>

      {/* 10. FOOTER COMERCIAL CALENDDY */}
      <footer className="w-full bg-[#191923] text-white pt-16 pb-12 px-4 sm:px-6 mt-auto">
        <div className="max-w-6xl mx-auto space-y-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="space-y-4 md:col-span-1">
              <CalenddyLogo variant="light" size="md" href="/" />
              <p className="text-xs text-slate-400 leading-relaxed">
                Plataforma SaaS integral de reservas, agenda multiprofesional, WhatsApp automatizado y pagos para negocios.
              </p>
            </div>

            <div className="space-y-3">
              <h5 className="text-xs font-bold text-white uppercase tracking-wider">Producto</h5>
              <ul className="space-y-2 text-xs text-slate-400">
                <li><Link href="/funciones" className="hover:text-white transition">Funciones</Link></li>
                <li><Link href="/precios" className="hover:text-white transition">Planes y Precios</Link></li>
                <li><a href="#integraciones" className="hover:text-white transition">WhatsApp & Mercado Pago</a></li>
                <li><Link href="/juanrojas" className="hover:text-white transition">Demo de Reservas</Link></li>
              </ul>
            </div>

            <div className="space-y-3">
              <h5 className="text-xs font-bold text-white uppercase tracking-wider">Negocios</h5>
              <ul className="space-y-2 text-xs text-slate-400">
                <li><Link href="/ingresar" className="hover:text-white transition">Iniciar Sesión</Link></li>
                <li><Link href="/registro" className="hover:text-white transition">Registrar mi Negocio</Link></li>
                <li><Link href="/dashboard" className="hover:text-white transition">Panel de Control</Link></li>
                <li><Link href="/mis-turnos" className="hover:text-white transition">Autogestión de Turnos</Link></li>
              </ul>
            </div>

            <div className="space-y-3">
              <h5 className="text-xs font-bold text-white uppercase tracking-wider">Contacto & Legal</h5>
              <ul className="space-y-2 text-xs text-slate-400">
                <li><a href="mailto:soporte@calenddy.com" className="hover:text-white transition">soporte@calenddy.com</a></li>
                <li><span className="text-slate-500">Buenos Aires, Argentina</span></li>
                <li><span className="text-slate-500">Términos del Servicio</span></li>
                <li><span className="text-slate-500">Política de Privacidad</span></li>
              </ul>
            </div>
          </div>

          <div className="pt-8 border-t border-slate-800 text-xs text-slate-500 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p>© 2026 Calenddy. Todos los derechos reservados.</p>
            <p className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              <span>Sistemas operando con normalidad (99.98% uptime)</span>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
